import { createFileRoute, redirect } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Alert } from '@osd/ui/alert';
import { Button } from '@osd/ui/button';
import { Card } from '@osd/ui/card';
import { Spinner } from '@osd/ui/spinner';
import { Heading } from '@osd/ui/heading';
import { Stack } from '@osd/ui/stack';
import { Text } from '@osd/ui/text';
import { api } from '../../lib/api';

/**
 * /admin/approvals — admin-only.
 *
 * The auth gate in `__root.tsx` ensures the visitor is signed in.
 * This route's `beforeLoad` enforces isAdmin (from a localStorage hint
 * we stash when /me answers) and bounces non-admins to /.
 */
export const Route = createFileRoute('/admin/approvals')({
  component: ApprovalsPage,
  beforeLoad: () => {
    if (typeof window === 'undefined') return;
    const isAdmin = localStorage.getItem('osd.auth.isAdmin') === 'true';
    if (!isAdmin) {
      throw redirect({ to: '/' });
    }
  },
});

interface Pending {
  id: string;
  name: string;
  email: string;
  createdAt: string;
}

function ApprovalsPage() {
  const qc = useQueryClient();
  const [feedback, setFeedback] = useState<string | null>(null);

  const pending = useQuery({
    queryKey: ['admin', 'approvals'],
    queryFn: async () => {
      const res = await api.admin().listPending();
      return res.pending as Pending[];
    },
    refetchInterval: 10_000,
  });

  const approve = useMutation({
    mutationFn: (id: string) => api.admin().approve(id),
    onSuccess: (res: { user: Pending }) => {
      qc.invalidateQueries({ queryKey: ['admin', 'approvals'] });
      setFeedback(`Approved ${res.user.email}. They can now sign in.`);
    },
    onError: (err) => setFeedback(`Approve failed: ${(err as Error).message}`),
  });

  const reject = useMutation({
    mutationFn: (id: string) => api.admin().reject(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'approvals'] });
      setFeedback('Rejected.');
    },
    onError: (err) => setFeedback(`Reject failed: ${(err as Error).message}`),
  });

  return (
    <Stack gap={5}>
      <header>
        <Heading level={1}>Pending registrations</Heading>
        <Text muted>
          New accounts land here for review. Approve to let them sign in; reject to discard the
          request.
        </Text>
      </header>

      {feedback !== null ? <Alert variant="info">{feedback}</Alert> : null}

      {pending.isLoading ? <Spinner size="sm" ariaLabel="Loading approvals" /> : null}
      {pending.isError ? (
        <Alert variant="danger">Failed to load approvals: {(pending.error as Error).message}</Alert>
      ) : null}

      {pending.data?.length === 0 ? (
        <Card>
          <Text muted>No pending registrations. You're all caught up.</Text>
        </Card>
      ) : null}

      <Stack gap={3}>
        {pending.data?.map((u) => (
          <Card key={u.id}>
            <Stack direction="row" align="center" justify="between" gap={3}>
              <Stack gap={1}>
                <strong>{u.name}</strong>
                <Text muted>{u.email}</Text>
                <Text muted>Registered {new Date(u.createdAt).toLocaleString()}</Text>
              </Stack>
              <Stack direction="row" gap={2}>
                <Button
                  variant="primary"
                  disabled={approve.isPending || reject.isPending}
                  onClick={() => approve.mutate(u.id)}
                >
                  Approve
                </Button>
                <Button
                  variant="danger"
                  disabled={approve.isPending || reject.isPending}
                  onClick={() => reject.mutate(u.id)}
                >
                  Reject
                </Button>
              </Stack>
            </Stack>
          </Card>
        ))}
      </Stack>
    </Stack>
  );
}
